/**
 * O ESQUEMA UNICO que precisa cobrir N0-N4.
 *
 * O truque e NAO usar o nivel como tag da uniao. O nivel e derivado; quem carrega o
 * comportamento e o par `(interaction.kind, stimulus.motion)`:
 *
 *   N0  select-one    + sem estimulo            aritmetica em texto puro
 *   N1  select-one    + StaticFigure            histograma gerado por parametros
 *   N2  select-one    + Animated/Manipulable    animacao ou slider
 *   N3  construct     + toolbelt curto          marcar pontos na malha
 *   N4  construct     + toolbelt de construcao  bissetriz com regua e compasso
 *
 * Assim o player e um unico `switch (interaction.kind)`, enquanto `motion` e `audio`
 * ficam como flags de apresentacao ORTOGONAIS. Nada proibe um item N4 com estimulo
 * animado: o esquema COMPOE em vez de enumerar combinacoes.
 */

import type { ConstructionSnapshot, GeoId, GeoObject } from '../geom/construction.ts';
import type { SnapConfig } from '../geom/snapping.ts';
import type { ToolId } from '../geom/tools.ts';
import type { Tol } from '../geom/tolerance.ts';
import type { Vec2 } from '../geom/vec.ts';
import type { NomeId } from '../core/lexico.ts';
import type { Rect, Scene, Shape } from '../render/scene.ts';
import type { Seed, Rng } from '../core/rng.ts';
import type { DifficultyEnvelope, DifficultySourceCode, ResponseModel } from './difficulty.ts';
import type { ErrorCode } from './errors.ts';
import type { PredicateOutcome } from './evidence.ts';
import type { RichText } from './richtext.ts';
import type {
  AltStructure,
  ChoiceId,
  CognitiveAxis,
  DeclaredDifficulty,
  SkillCode,
  TaskModelCode,
} from './taxonomy.ts';

export type InteractivityLevel = 'N0' | 'N1' | 'N2' | 'N3' | 'N4';

/**
 * O flag de audio, como TIPO e nao como convencao.
 *
 * Audio nao e um detalhe de midia: implica fone por aluno, isolamento acustico ou
 * aplicacao individual. Um item com `need: 'required'` e INAPLICAVEL em sala sem
 * infraestrutura -- e o player o recusa em modo `exam`, de modo que a regra deixa de
 * depender de alguem lembrar dela na hora de montar a prova.
 */
export type AudioRequirement =
  | { readonly need: 'none' }
  | { readonly need: 'optional'; readonly src: string; readonly transcript: RichText }
  | { readonly need: 'required'; readonly src: string; readonly transcript: RichText };

export interface StimulusBase {
  readonly id: string;
  readonly caption?: RichText;
  /** Descricao textual OBRIGATORIA. Tambem e o fallback de impressao e de braille. */
  readonly alt: string;
}

export interface StaticFigure extends StimulusBase {
  readonly kind: 'static-figure';
  readonly motion: 'none';
  readonly audio: { readonly need: 'none' };
  readonly scene: Scene;
}

export interface AnimatedFigure extends StimulusBase {
  readonly kind: 'animated';
  readonly motion: 'animated';
  readonly audio: AudioRequirement;
  /** Funcao PURA do tempo normalizado t em [0,1]. Determinista e testavel por snapshot. */
  readonly frame: (t: number) => Scene;
  readonly durationMs: number;
  readonly loop: boolean;
  /** Quadro mostrado sob `prefers-reduced-motion` e na impressao. */
  readonly staticFallbackAt: number;
  /**
   * Trecho horizontal da cena, em unidades de modelo, que a BARRA DE TEMPO deve cobrir.
   *
   * Existe para o item de percurso: a barra de tempo fica logo abaixo da estrada e tem
   * de ter exatamente o comprimento dela, com o cursor alinhado ao inicio e ao fim do
   * trajeto. So assim a comparacao que o item quer provocar — o movel ja andou metade
   * do caminho, mas ainda nao passou metade do tempo — e visivel a olho. Sem este
   * dado, o player estica a barra na largura toda e ela sai mais longa que a estrada.
   */
  readonly timeline?: { readonly x0: number; readonly x1: number };
}

export interface ControlSpec {
  readonly id: string;
  readonly label: RichText;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly initial: number;
  readonly format: (v: number) => string;
}

export interface ManipulableFigure extends StimulusBase {
  readonly kind: 'manipulable';
  readonly motion: 'interactive';
  readonly audio: AudioRequirement;
  readonly controls: readonly ControlSpec[];
  /** Funcao PURA dos valores dos controles. */
  readonly frame: (params: Readonly<Record<string, number>>) => Scene;
  /**
   * O quadro que vai para o PAPEL, quando não é o inicial: a caixa já planificada, e
   * não fechada. Sem ele, a prova impressa usa os valores iniciais dos controles.
   */
  readonly quadroImpresso?: Readonly<Record<string, number>>;
}

export interface DataTable extends StimulusBase {
  readonly kind: 'table';
  readonly motion: 'none';
  readonly audio: { readonly need: 'none' };
  readonly headers: readonly RichText[];
  readonly rows: readonly (readonly RichText[])[];
  readonly caption?: RichText;
}

/**
 * FIGURA COM RÉGUA: uma figura parada e, por cima dela, uma régua graduada que o aluno
 * arrasta e gira para medir — a distância entre duas cidades num mapa, o lado de uma
 * figura. A régua é FERRAMENTA, não resposta: o item continua N2, e a resposta vem da
 * interação (em geral, o número digitado).
 */
export interface MeasurableFigure extends StimulusBase {
  readonly kind: 'measurable';
  readonly motion: 'interactive';
  readonly audio: { readonly need: 'none' };
  readonly scene: Scene;
  readonly regua: {
    /** Comprimento graduado, em unidades da régua. */
    readonly comprimento: number;
    /** Unidades de desenho por unidade da régua: é o que faz a régua medir a figura. */
    readonly escala: number;
    /** Marcas entre duas unidades inteiras (10 = milímetros). */
    readonly subdivisoes: number;
    readonly unidade: string;
    /** Onde fica o zero da régua, em coordenadas do desenho (y para cima). */
    readonly inicio: Vec2;
    /** Em graus, no sentido anti-horário. */
    readonly angulo: number;
  };
}

export type Stimulus =
  StaticFigure | AnimatedFigure | ManipulableFigure | DataTable | MeasurableFigure;

/** O item pode ser aplicado numa sala de aula comum? */
export const isExamSafe = (s: Stimulus): boolean => s.audio.need !== 'required';

// ---------------------------------------------------------------------------
// Interacao
// ---------------------------------------------------------------------------

export interface Alternative {
  readonly id: ChoiceId;
  readonly content: RichText;
  /** Alternativas cujo corpo e uma figura (estrutura E6). */
  readonly figure?: Scene;
  /** `null` na chave; o erro nomeado em cada um dos 4 distratores. */
  readonly error: ErrorCode | null;
  /** O valor bruto calculado; usado nas invariantes e na analitica. */
  readonly rawValue: number | string | null;
  /**
   * A conta que o erro faz, COM OS NUMEROS DESTA VARIANTE: "(60 + 90) / 2 = 75".
   *
   * A descricao do catalogo diz qual e o erro de logica em geral; esta explicacao
   * mostra ao aluno o caminho exato que o levou a esta alternativa. Como o distrator
   * nasceu de um solucionador defeituoso, o sistema sabe reconstituir a conta — o que
   * um banco de itens escritos a mao nao sabe. Nunca vai ao navegador em modo exame.
   */
  readonly explanation?: RichText;
}

export interface SelectOneInteraction {
  readonly kind: 'select-one';
  readonly structure: AltStructure;
  readonly alternatives: readonly [Alternative, Alternative, Alternative, Alternative, Alternative];
  readonly keyId: ChoiceId;
}

export interface NumericEntryInteraction {
  readonly kind: 'numeric-entry';
  readonly unit?: string;
  readonly decimals: number;
  readonly keyValue: number;
  readonly absTol: number;
  /** Saidas dos solucionadores defeituosos: uma resposta digitada errada ainda recebe diagnostico. */
  readonly errorValues: readonly {
    readonly value: number;
    readonly error: ErrorCode;
    /** A conta do erro, com os numeros da variante — ver `Alternative.explanation`. */
    readonly explanation?: RichText;
  }[];
  /**
   * FAIXA de respostas corretas, quando o problema admite mais de uma — "um valor de
   * abril que mantenha o crescimento" aceita qualquer um entre marco e maio. Limites
   * INCLUSIVOS. Com ela, `keyValue` passa a ser so um representante da faixa.
   */
  readonly accept?: { readonly lo: number; readonly hi: number };
  /**
   * Diagnostico por FAIXA: respostas erradas que caem num intervalo tipico de erro
   * ("abril acima de maio quebra o crescimento"). Consultado depois de `errorValues`.
   */
  readonly errorIntervals?: readonly {
    readonly lo: number;
    readonly hi: number;
    readonly error: ErrorCode;
    readonly explanation?: RichText;
  }[];
  /**
   * A resposta NAO e digitada: e o valor de um controle de um estimulo manipulavel.
   * O aluno responde agindo sobre a figura — por isso o item e N3, e nao N2.
   */
  readonly viaControl?: { readonly stimulusId: string; readonly controlId: string };
}

/**
 * PREENCHIMENTO DE COORDENADAS: uma ficha — a figura desenhada à parte — mostra alguns
 * pontos com as coordenadas já escritas e, nos outros, os espaços para o aluno
 * completar. É a forma do item do quadrado no GeoGebra. Resposta digitada, mas em
 * vários campos que só fazem sentido juntos.
 */
export interface CoordinateEntryInteraction {
  readonly kind: 'coordinate-entry';
  readonly ficha: Scene;
  /** Os pontos da ficha. Sem `valor`, o ponto é PEDIDO e vira dois campos. */
  readonly pontos: readonly {
    readonly nome: string;
    /** Onde o rótulo fica, em coordenadas da ficha. */
    readonly em: Vec2;
    /** Para que lado do ponto o rótulo cresce. */
    readonly lado: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
    readonly valor?: Vec2;
  }[];
  /** Respostas aceitas, na ordem dos pontos pedidos. A ordem entre eles não importa. */
  readonly respostas: readonly (readonly Vec2[])[];
  /** Diagnóstico por conjunto de pontos: cada erro típico, com a conta que o produz. */
  readonly erros: readonly {
    readonly pontos: readonly Vec2[];
    readonly error: ErrorCode;
    readonly explanation?: RichText;
  }[];
}

/**
 * ARRASTAR PONTOS: uma figura já está na tela, fora do lugar, e o aluno leva os
 * vértices dela até onde devem ficar. É a forma do item de simetria do GeoGebra — o
 * segundo triângulo existe desde o início; o trabalho é acertá-lo. Os pontos encaixam
 * nos cruzamentos da malha e também andam pelas setas do teclado.
 */
export interface DragPointsInteraction {
  readonly kind: 'drag-points';
  /** Descrição textual da figura, para leitor de tela: onde está cada coisa. */
  readonly descricao: string;
  readonly backdrop: readonly Shape[];
  readonly viewBox: Rect;
  /** Os pontos arrastáveis, em coordenadas do desenho. */
  readonly pontos: readonly { readonly nome: string; readonly inicial: Vec2 }[];
  /** Pares de pontos ligados por segmento: o contorno da figura que se arrasta. */
  readonly ligacoes: readonly (readonly [number, number])[];
  /** Passo da malha em que os pontos encaixam, em unidades do desenho. */
  readonly passo: number;
  readonly alvo: DragTarget;
  /** O ponto só anda num eixo: 'x' (reta numérica) ou 'y' (altura de uma coluna). */
  readonly trava?: 'x' | 'y';
  /**
   * Como os pontos aparecem: 'figura' (vértices ligados, o padrão), 'marcadores'
   * (alfinetes com haste até a base, para a reta numérica) ou 'barras' (colunas que
   * sobem da base até o ponto).
   */
  readonly desenho?: 'figura' | 'marcadores' | 'barras';
  /** Unidades do desenho por unidade de medida — para dizer a posição ao leitor de tela. */
  readonly unidade?: number;
  /** A altura (y) de onde partem as hastes e as colunas. Padrão: 0. */
  readonly base?: number;
  readonly larguraBarra?: number;
  /** Onde os pontos podem ficar. Padrão: o enquadramento, com margem. */
  readonly limites?: Rect;
  /**
   * Índices dos pontos que NÃO se movem — fazem parte da figura, mas já estão no lugar
   * (o A' de uma ampliação). Desenhados como os outros, sem alça.
   */
  readonly fixos?: readonly number[];
}

export interface DragTarget {
  readonly describe: RichText;
  readonly correto: (pos: readonly Vec2[]) => boolean;
  /** Rodam só quando `correto` falha — as âncoras de erro, como nos itens de construção. */
  readonly diagnostics: readonly {
    readonly error: ErrorCode;
    readonly test: (pos: readonly Vec2[]) => boolean;
  }[];
  readonly partialCredit?: readonly {
    readonly id: string;
    readonly label: string;
    readonly weight: number;
    readonly test: (pos: readonly Vec2[]) => boolean;
  }[];
  readonly analyze?: (pos: readonly Vec2[]) => RichText;
  readonly reviewShapes?: (pos: readonly Vec2[]) => readonly Shape[];
  readonly reviewLegend?: RichText;
}

/**
 * ENCONTRE O ERRO: a resolução de um estudante fictício, passo a passo, com um erro, e
 * o aluno aponta o PRIMEIRO passo errado. Os passos seguintes são coerentes com o erro
 * — herdam o valor errado e fazem certo o resto —, e por isso só um passo é a chave.
 *
 * Mede o que a múltipla escolha não mede: reconhecer o erro num raciocínio alheio, e
 * não só evitá-lo no próprio. É a habilidade "avaliar a correção de um procedimento".
 */
export interface ErrorStepInteraction {
  readonly kind: 'error-step';
  readonly passos: readonly RichText[];
  /** Índice (a partir de 0) do primeiro passo errado: a chave. */
  readonly passoErrado: number;
  /** O erro que a resolução comete, nomeado. */
  readonly erro: ErrorCode;
  /** O que está errado no passo e como ele fica certo, com os números da variante. */
  readonly explicacao: RichText;
}

/**
 * ORDENAR: cartões que o aluno põe em ordem, arrastando ou pelos botões de subir e
 * descer. A resposta é a sequência inteira; os erros típicos são sequências inteiras
 * também — a ordem que um raciocínio equivocado produz.
 */
export interface OrderInteraction {
  readonly kind: 'order';
  /** Os cartões, na ordem em que aparecem de início. */
  readonly itens: readonly RichText[];
  /** A ordem certa: índices de `itens`, do primeiro ao último. */
  readonly ordem: readonly number[];
  /** O que significam as pontas da lista: ["menor", "maior"]. */
  readonly pontas: readonly [string, string];
  readonly erros: readonly {
    readonly ordem: readonly number[];
    readonly error: ErrorCode;
    readonly explanation?: RichText;
  }[];
}

/**
 * EXPRESSÃO DIGITADA: o aluno escreve uma expressão numa variável ("3n + 2"). A
 * correção é por EQUIVALÊNCIA — as expressões são calculadas em vários valores —, e
 * não por comparação de texto: "2 + 3n" e "3(n − 1) + 5" também estão certas.
 */
export interface ExpressionEntryInteraction {
  readonly kind: 'expression-entry';
  readonly variavel: string;
  /** O que vem antes do campo: "aₙ =" ou "f(x) =". */
  readonly prefixo: string;
  /** Um exemplo de escrita, com outros números, mostrado sob o campo. */
  readonly exemplo: string;
  readonly chave: string;
  /** Valores da variável em que as expressões são comparadas. */
  readonly amostras: readonly number[];
  readonly erros: readonly {
    readonly expr: string;
    readonly error: ErrorCode;
    readonly explanation?: RichText;
  }[];
}

/**
 * AJUSTAR ATÉ COINCIDIR: a figura tem VÁRIOS controles, e a resposta é o conjunto dos
 * valores em que o aluno os deixou — mover a e b até a reta y = ax + b passar pelos
 * pontos. Um controle só não basta: o que se mede é coordenar os dois. Correção por
 * predicado sobre os valores, com diagnóstico e crédito parcial, como no arrastar.
 */
export interface ControlFitInteraction {
  readonly kind: 'control-fit';
  readonly stimulusId: string;
  /** Os controles do estímulo que compõem a resposta. */
  readonly controles: readonly string[];
  readonly alvo: ControlTarget;
}

export interface ControlTarget {
  readonly describe: RichText;
  readonly correto: (v: Readonly<Record<string, number>>) => boolean;
  readonly diagnostics: readonly {
    readonly error: ErrorCode;
    readonly test: (v: Readonly<Record<string, number>>) => boolean;
  }[];
  readonly partialCredit?: readonly {
    readonly id: string;
    readonly label: string;
    readonly weight: number;
    readonly test: (v: Readonly<Record<string, number>>) => boolean;
  }[];
  readonly analyze?: (v: Readonly<Record<string, number>>) => RichText;
}

/**
 * CLICAR EM REGIÕES: uma figura dividida em partes — as 36 casas de dois dados, seis
 * figuras lado a lado — e o aluno marca TODAS as que cumprem o pedido. A resposta é
 * um conjunto; o erro típico também é um conjunto ("marcou só metade das casas, uma
 * por par de números") ou uma regra sobre ele ("marcou uma figura com um bloco 2 × 2").
 */
export interface RegionSelectInteraction {
  readonly kind: 'region-select';
  /** Descrição textual da figura, para leitor de tela. */
  readonly descricao: string;
  readonly backdrop: readonly Shape[];
  readonly viewBox: Rect;
  readonly regioes: readonly Regiao[];
  /**
   * O que se marca — "casa", "figura", "polígono" —, como chave do léxico. Vai na
   * dica, na contagem, no retorno e no gabarito impresso.
   *
   * Eram dois campos, o texto no plural e um `genero` à parte, e cada uma das cinco
   * frases que os usavam concordava à mão: o singular saía por amputação
   * (`oQue.replace(/s$/, '')`) e uma delas, fixa no feminino, dizia "As pedidas eram
   * 3" sobre polígonos.
   */
  readonly oQue: NomeId;
  readonly chave: readonly string[];
  /** Conjuntos que são a saída de um erro típico, com a conta dele. */
  readonly erros: readonly {
    readonly conjunto: readonly string[];
    readonly error: ErrorCode;
    readonly explanation?: RichText;
  }[];
  /** Erros por REGRA, consultados quando nenhum conjunto casa. */
  readonly diagnosticos?: readonly {
    readonly error: ErrorCode;
    readonly test: (sel: readonly string[]) => boolean;
  }[];
  readonly analisar?: (sel: readonly string[]) => RichText;
}

export interface Regiao {
  readonly id: string;
  /** O contorno, em coordenadas do desenho (y para cima). */
  readonly forma: readonly Vec2[];
  /** Como a região é citada no texto: "(2, 5)", "B". */
  readonly rotulo: string;
  /** Como o leitor de tela a anuncia: "azul 2, vermelho 5". */
  readonly nome: string;
}

export interface RubricCriterion {
  readonly id: string;
  readonly label: string;
  readonly weight: number;
  readonly test: (
    objs: ReadonlyMap<GeoId, GeoObject>,
    givens: ReadonlyMap<GeoId, GeoObject>,
    tol: Tol,
  ) => boolean;
}

export interface DiagnosticPredicate {
  readonly error: ErrorCode;
  readonly test: (
    objs: ReadonlyMap<GeoId, GeoObject>,
    givens: ReadonlyMap<GeoId, GeoObject>,
    tol: Tol,
  ) => boolean;
}

export interface ConstructionTarget {
  readonly describe: RichText;
  /** O objetivo semantico, avaliado sobre a construcao ja calculada. */
  readonly predicate: (
    objs: ReadonlyMap<GeoId, GeoObject>,
    givens: ReadonlyMap<GeoId, GeoObject>,
    tol: Tol,
  ) => PredicateOutcome;
  /** Rodam SO quando `predicate` falha -- sao as ancoras de erro do N4. */
  readonly diagnostics: readonly DiagnosticPredicate[];
  /** Quantas reavaliacoes perturbadas precisam passar tambem. */
  readonly robustnessTrials: number;
  readonly partialCredit?: readonly RubricCriterion[];
  /**
   * Analise DESTA resposta, para o comentario detalhado: quais pontos faltaram, quais
   * sobraram, o que a construcao tem e o que lhe falta. O diagnostico diz o nome do
   * erro; a analise diz onde ele esta na figura do aluno.
   */
  readonly analyze?: (
    objs: ReadonlyMap<GeoId, GeoObject>,
    givens: ReadonlyMap<GeoId, GeoObject>,
    tol: Tol,
  ) => RichText;
  /**
   * Marcas desenhadas por cima da construcao do aluno quando ele REVE o item: acerto,
   * falta e sobra, cada um com seu papel visual. Opcional — sem elas, a revisao mostra
   * a construcao como ela ficou.
   */
  readonly reviewShapes?: (
    objs: ReadonlyMap<GeoId, GeoObject>,
    givens: ReadonlyMap<GeoId, GeoObject>,
    tol: Tol,
  ) => readonly Shape[];
  /** Legenda das marcas de revisão: o que cada cor e cada traço quer dizer. */
  readonly reviewLegend?: RichText;
}

export interface ConstructInteraction {
  readonly kind: 'construct';
  /** O cinto. Um item de marcar pontos na malha e so `['point','undo']`. */
  readonly toolbelt: readonly ToolId[];
  /** Os dados do problema, ja como DAG. */
  readonly givens: ConstructionSnapshot;
  /**
   * Camada DECORATIVA desenhada sob a construcao — regiao sombreada, moldura, escala.
   *
   * Existe porque nem todo contexto e um objeto construtivel. Numa primeira versao do
   * item de marcar pontos na malha, a regiao foi posta no DAG como tres pontos dados
   * ligados por segmentos; o resultado foi que os pontos da resposta que caiam sobre
   * os vertices prendiam no ponto JA EXISTENTE em vez de criar uma marcacao — e a
   * resposta correta ficava impossivel de produzir. Cenario e cenario; DAG e DAG.
   */
  readonly backdrop?: readonly Shape[];
  /** Enquadramento fixo. Sem ele, os limites saem dos `givens` — que podem ser vazios. */
  readonly viewBox?: Rect;
  /** Quais dados podem ser perturbados na checagem de robustez, e ate onde. */
  readonly perturbable: readonly { readonly id: GeoId; readonly radius: number }[];
  readonly target: ConstructionTarget;
  readonly snapping: SnapConfig;
  readonly maxActions?: number;
  /**
   * Dar letra aos pontos que o aluno cria. Padrão: sim — numa construção, "a
   * interseção dos arcos em C" é o que permite conversar sobre ela. Num item de marcar
   * muitos pontos, as letras só poluem a figura.
   */
  readonly labelCreatedPoints?: boolean;
  /**
   * Quando o aluno tiver marcado exatamente este número de pontos, a prancheta os liga
   * num polígono, na ordem em que foram marcados. Só desenho: a correção continua sendo
   * sobre os pontos. No triângulo simétrico, ver o triângulo pronto ajuda a conferir.
   */
  readonly ligarPontos?: number;
  /**
   * ECONOMIA, no estilo do Euclidea: o número de movimentos da construção mais curta que
   * conhecemos. Construção correta com essa quantidade ou menos ganha três estrelas; com
   * até dois a mais, duas; além disso, uma. As estrelas NÃO entram na nota — a correção
   * é a mesma —, só no retorno ao aluno.
   */
  readonly meta?: number;
}

export type Interaction =
  | SelectOneInteraction
  | NumericEntryInteraction
  | ConstructInteraction
  | CoordinateEntryInteraction
  | DragPointsInteraction
  | ErrorStepInteraction
  | OrderInteraction
  | ExpressionEntryInteraction
  | ControlFitInteraction
  | RegionSelectInteraction
  | BalanceOpsInteraction;

// ---------------------------------------------------------------------------
// BALANCA DE DOIS PRATOS: resolver uma equacao mexendo nos pratos
// ---------------------------------------------------------------------------

/** Um prato: quantos sacos (cada um com `x` dentro) e quantos pesos de 1. */
export interface Prato {
  readonly sacos: number;
  readonly pesos: number;
}

export interface EstadoBalanca {
  readonly esq: Prato;
  readonly dir: Prato;
}

/**
 * Uma acao do aluno sobre a balanca.
 *
 *  - `tirar-peso` e `tirar-saco` valem para UM prato, e e essa a decisao central do
 *    item: a operacao e por prato, nao pelos dois de uma vez. Tirar de um lado so
 *    desequilibra, e a balanca mostra na hora — que e exatamente a licao.
 *  - `repartir` vale para os dois pratos ao mesmo tempo, porque repartir um prato so
 *    nao e uma operacao que alguem faca por engano: e outra conta.
 */
export type AcaoBalanca =
  | { readonly t: 'tirar-peso'; readonly prato: 'esq' | 'dir' }
  | { readonly t: 'tirar-saco'; readonly prato: 'esq' | 'dir' }
  | { readonly t: 'repartir'; readonly partes: number };

/**
 * BALANCA DE DOIS PRATOS — a construcao da algebra.
 *
 * A equacao aparece como objetos: sacos iguais, todos com a mesma quantidade dentro, e
 * pesos de 1 kg. Resolver e ir tirando coisa dos pratos ate sobrar UM saco sozinho de
 * um lado e so pesos do outro — e ai o saco vale o que os pesos mostram.
 *
 * A RESPOSTA E A TRAJETORIA, como nos itens de construcao. O estado final sozinho nao
 * distingue quem fez algebra de quem foi tirando peca ate a balanca ficar reta: quem
 * tira dois sacos de um prato so e dez pesos do outro tambem chega a um estado
 * equilibrado, por um caminho que nao e valido. Corrigindo a trajetoria, esse caminho
 * vira um ERRO COM NOME — "tirou de um prato so" — em vez de passar por acerto.
 *
 * A balanca INCLINA, ao contrario da prancha da gangorra. La inclinar entregaria a
 * resposta; aqui inclinar e a definicao de equacao: os dois pratos pesam o mesmo, e
 * quem tira de um lado so ve na hora o que fez.
 */
export interface BalanceOpsInteraction {
  readonly kind: 'balance-ops';
  readonly descricao: string;
  /** Como comeca: a equacao, em objetos. */
  readonly inicial: EstadoBalanca;
  /**
   * O recipiente, como o enunciado o chama — "saco", "pote", "saquinho" —, para a
   * tela, a descricao acessivel e o gabarito falarem do mesmo objeto que o texto.
   *
   * Havia um campo so, `nomeSaco`, com dois papeis: o esquema o documentava como
   * "saco"/"caixa" e o item passava "x". O gabarito impresso saia "Cada x vale 4 kg", e
   * a interface dizia "saco" na variante dos potes.
   */
  readonly recipiente: NomeId;
  /** O rotulo desenhado em cada recipiente: "x". */
  readonly incognita: string;
  /** A unidade dos pesos: "kg". */
  readonly unidade: string;
  /** Menor numero de acoes que resolve — a meta das estrelas. */
  readonly meta: number;
  /** O valor de `x`: e a chave, e sai da versao publica. */
  readonly valor: number;
  readonly alvo: BalanceTarget;
}

export interface BalanceTarget {
  readonly describe: RichText;
  /** Rodam so quando o estado final ou a trajetoria falham. */
  readonly diagnostics: readonly {
    readonly error: ErrorCode;
    readonly test: (estado: EstadoBalanca, acoes: readonly AcaoBalanca[]) => boolean;
  }[];
  readonly partialCredit?: readonly {
    readonly id: string;
    readonly label: string;
    readonly weight: number;
    readonly test: (estado: EstadoBalanca, acoes: readonly AcaoBalanca[]) => boolean;
  }[];
  readonly analyze?: (estado: EstadoBalanca, acoes: readonly AcaoBalanca[]) => RichText;
}

// ---------------------------------------------------------------------------
// Template
// ---------------------------------------------------------------------------

export type TemplateId = string & { readonly __template: unique symbol };
export const templateId = (s: string): TemplateId => s as TemplateId;

export interface SolveCtx {
  readonly rng: Rng;
  readonly round: (x: number, dp?: number) => number;
}

/** Marcador para "este erro nao pode ocorrer com estes parametros". */
export const NOT_APPLICABLE = Symbol('not-applicable');
export type Maybe<A> = A | typeof NOT_APPLICABLE;

/**
 * A abstracao central do projeto.
 *
 * `solve` NAO e uma perturbacao da chave: e um CAMINHO DE SOLUCAO COMPLETO e
 * alternativo, executado sobre os mesmos parametros. E o que faz cada distrator ser
 * o resultado de um erro nomeado e documentavel -- exigencia que o catalogo CAEd ja
 * fazia para os itens escritos a mao, e que aqui vira codigo.
 */
export interface BuggySolver<P, A> {
  readonly error: ErrorCode;
  /** Ordem de preferencia; menor = mais valioso pedagogicamente, tentado primeiro. */
  readonly priority: number;
  readonly solve: (params: P, ctx: SolveCtx) => Maybe<A>;
  /**
   * A mesma conta errada, escrita para o aluno, com os numeros desta variante.
   *
   * Recebe o valor que `solve` produziu, para a explicacao nunca divergir da
   * alternativa que o aluno viu. E o que permite ao comentario dizer "voce fez
   * (60 + 90) / 2 = 75" em vez de apenas "voce errou".
   */
  readonly explain?: (params: P, value: A) => RichText;
}

export interface ResolvedDistractor<A> {
  readonly error: ErrorCode;
  readonly value: A;
  readonly explanation?: RichText;
}

export interface BuiltItem {
  /** Texto-base: o contexto, ANTES do suporte. */
  readonly stem: RichText;
  /**
   * Comando: a pergunta propriamente dita, DEPOIS do suporte.
   *
   * A separacao nao e cosmetica. Um item se le em tres tempos — contexto, suporte,
   * pergunta — e o comando tem de ser a ultima coisa antes das alternativas. Se a
   * pergunta vier antes da figura, o aluno le, esquece, olha a figura e volta a ler.
   * E a anatomia que o proprio catalogo CAEd usa nos itens escritos a mao:
   * texto-base, comando, alternativas.
   */
  readonly comando?: RichText;
  readonly stimuli: readonly Stimulus[];
  readonly interaction: Interaction;
  /** Resolucao comentada, para o professor. NUNCA vai ao navegador em modo exame. */
  readonly rationale: RichText;
  readonly declared?: DeclaredDifficulty;
  /**
   * DICAS GRADUADAS: da mais geral à mais específica, com os números da variante — e
   * nenhuma é a resposta. O aluno as pede uma a uma, no treino; no exame não aparecem.
   * Não são gabarito, e por isso vão na versão pública.
   */
  readonly dicas?: readonly RichText[];
}

export interface ItemTemplate<P = unknown, A = unknown> {
  readonly id: TemplateId;
  /** SOBE quando o gerador muda de comportamento. Entra na semente. */
  readonly version: number;
  readonly taskModel: TaskModelCode;
  readonly skills: readonly SkillCode[];
  readonly axis: CognitiveAxis;
  readonly level: InteractivityLevel;
  readonly title: string;
  readonly responseModel: ResponseModel;

  /**
   * Excecao justificada. Por padrao o piso NAO e escolhido pelo template: ele e
   * derivado do eixo cognitivo e do ano da habilidade, por `pisoDeDificuldade`.
   * Um numero escolhido a olho por item nao e comparavel com o dos outros.
   */
  readonly difficultyBase?: number;
  readonly difficultySources: readonly DifficultySourceCode[];

  /** 1. sorteia e restringe os parametros (pode lancar `SamplingFailure`) */
  readonly params: (rng: Rng) => P;
  /** 2. a chave: o caminho de solucao correto */
  readonly solve: (params: P, ctx: SolveCtx) => A;
  /** 3. variantes defeituosas; >= 4, idealmente 6-7 para folga de reposicao */
  readonly buggy: readonly BuggySolver<P, A>[];
  /** 4. modificadores de dificuldade calculados a partir dos parametros reais */
  readonly variantDifficulty: (params: P, key: A) => readonly DifficultySourceCode[];
  /** 5. monta enunciado, estimulos e interacao */
  readonly build: (
    params: P,
    key: A,
    distractors: readonly ResolvedDistractor<A>[],
    rng: Rng,
  ) => BuiltItem;
  /** 6. checagens extras de validade; devolve a mensagem do problema ou `null` */
  readonly validate?: (built: BuiltItem, params: P) => string | null;
  /** Comparacao de respostas, para deteccao de colisao entre distratores. */
  readonly equals?: (x: A, y: A) => boolean;
  /**
   * Separacao relativa minima entre alternativas numericas. Padrao: 0,02.
   *
   * A regra dos 2% vem do catalogo e guarda contra DUAS ALTERNATIVAS NA TELA parecerem
   * a mesma coisa. Num item de resposta digitada nao ha alternativas lado a lado: o
   * que precisa ser garantido e que dois diagnosticos nao se confundam, e isso se mede
   * pela tolerancia da resposta, nao por 2%. Templates de `numeric-entry` podem, e
   * devem, baixar este valor — mantendo-o bem acima de `absTol`.
   */
  readonly distractorMinRelSep?: number;
  /**
   * Quantas alternativas o item de múltipla escolha tem, contando a chave. Padrão: 5.
   *
   * Existe por um pedido do autor: no item do eixo truncado, a pergunta "essa afirmação
   * se sustenta?" tem duas respostas naturais — sustenta-se pelas alturas desenhadas,
   * ou não se sustenta pelos valores —, e as outras três alternativas eram enchimento
   * que ninguém marcaria.
   */
  readonly alternativas?: number;
}

// ---------------------------------------------------------------------------
// Instancia
// ---------------------------------------------------------------------------

export interface ItemInstance {
  readonly templateId: TemplateId;
  readonly templateVersion: number;
  readonly seed: Seed;
  /** Hash da instancia canonizada e sem chave: o recibo de reprodutibilidade. */
  readonly instanceHash: string;
  readonly level: InteractivityLevel;
  readonly taskModel: TaskModelCode;
  readonly skills: readonly SkillCode[];
  readonly axis: CognitiveAxis;
  readonly title: string;
  /** Texto-base: o contexto, ANTES do suporte. */
  readonly stem: RichText;
  /** Comando: a pergunta, DEPOIS do suporte e imediatamente antes das alternativas. */
  readonly comando: RichText;
  readonly stimuli: readonly Stimulus[];
  readonly interaction: Interaction;
  readonly difficulty: DifficultyEnvelope;
  readonly rationale: RichText;
  /** Dicas graduadas — ver `BuiltItem.dicas`. */
  readonly dicas?: readonly RichText[];
  /** Informativo; excluido do hash de proposito. */
  readonly generatedAt: string;
}

/**
 * Ferramentas de CONTROLE, nao de construcao. A presenca delas nao eleva o nivel:
 * marcar pontos e poder apagar ou desfazer e N3; tracar retas e arcos e N4.
 */
export const FERRAMENTAS_DE_CONTROLE: ReadonlySet<ToolId> = new Set<ToolId>([
  'select',
  'undo',
  'delete',
  'point',
]);

/** Nivel derivado do par (interacao, movimento). */
export function levelOf(i: Pick<ItemInstance, 'interaction' | 'stimuli'>): InteractivityLevel {
  if (i.interaction.kind === 'construct') {
    return i.interaction.toolbelt.some((t) => !FERRAMENTAS_DE_CONTROLE.has(t)) ? 'N4' : 'N3';
  }
  // Responder MOVENDO um controle da figura e agir sobre ela: N3, nao N2. No N2 a
  // manipulacao e ferramenta de pensamento; aqui ela e a propria resposta.
  // Arrastar os vértices de uma figura até o lugar é agir sobre ela: N3.
  if (i.interaction.kind === 'drag-points') return 'N3';
  // Arrastar cartões para ordená-los também é agir sobre os objetos da questão.
  if (i.interaction.kind === 'order') return 'N3';
  if (i.interaction.kind === 'numeric-entry' && i.interaction.viaControl) return 'N3';
  if (i.interaction.kind === 'control-fit') return 'N3';
  // Marcar as partes da figura que cumprem o pedido é agir sobre ela.
  if (i.interaction.kind === 'region-select') return 'N3';
  // Mexer nos pratos até sobrar um saco sozinho é construir: há um cinto de operações,
  // uma trajetória gravada e uma meta de economia, como nas construções com compasso.
  if (i.interaction.kind === 'balance-ops') return 'N4';
  if (i.stimuli.some((s) => s.motion !== 'none')) return 'N2';
  if (i.stimuli.length > 0) return 'N1';

  // Estrutura E6: as ALTERNATIVAS sao figuras geradas. O item nao tem estimulo
  // separado, mas e visual e dinamico do mesmo jeito — tratá-lo como N0 (texto puro)
  // esconderia dele todas as consequencias de acessibilidade e impressao.
  if (i.interaction.kind === 'select-one' && i.interaction.alternatives.some((a) => a.figure)) {
    return 'N1';
  }

  return 'N0';
}

// ---------------------------------------------------------------------------
// Separacao chave / publico
// ---------------------------------------------------------------------------

export type PublicAlternative = Omit<Alternative, 'error' | 'rawValue' | 'explanation'>;

export type PublicInteraction =
  | (Omit<SelectOneInteraction, 'keyId' | 'alternatives'> & {
      readonly alternatives: readonly PublicAlternative[];
    })
  | Omit<NumericEntryInteraction, 'keyValue' | 'errorValues' | 'accept' | 'errorIntervals'>
  | (Omit<ConstructInteraction, 'target' | 'perturbable'> & {
      readonly target: { readonly describe: RichText };
    })
  | Omit<CoordinateEntryInteraction, 'respostas' | 'erros'>
  | (Omit<BalanceOpsInteraction, 'alvo' | 'valor'> & {
      readonly alvo: { readonly describe: RichText };
    })
  | (Omit<DragPointsInteraction, 'alvo'> & { readonly alvo: { readonly describe: RichText } })
  | Omit<ErrorStepInteraction, 'passoErrado' | 'erro' | 'explicacao'>
  | Omit<OrderInteraction, 'ordem' | 'erros'>
  | Omit<ExpressionEntryInteraction, 'chave' | 'erros' | 'amostras'>
  | (Omit<ControlFitInteraction, 'alvo'> & { readonly alvo: { readonly describe: RichText } })
  | Omit<RegionSelectInteraction, 'chave' | 'erros' | 'diagnosticos' | 'analisar'>;

export type PublicItemInstance = Omit<ItemInstance, 'interaction' | 'rationale' | 'difficulty'> & {
  readonly interaction: PublicInteraction;
  readonly difficulty: Pick<DifficultyEnvelope, 'provisionalB' | 'status'>;
};

/**
 * O que o navegador pode receber em modo exame.
 *
 * Separar os tipos custa 20 linhas agora e evita a descoberta constrangedora de que
 * o gabarito estava no payload. Ha um teste que varre o JSON serializado atras do
 * valor da chave.
 */
export function stripKey(i: ItemInstance): PublicItemInstance {
  const inter = i.interaction;

  let publicInteraction: PublicInteraction;
  if (inter.kind === 'select-one') {
    publicInteraction = {
      kind: 'select-one',
      structure: inter.structure,
      alternatives: inter.alternatives.map(({ id, content, figure }) =>
        figure === undefined ? { id, content } : { id, content, figure },
      ),
    };
  } else if (inter.kind === 'numeric-entry') {
    const { keyValue: _k, errorValues: _e, accept: _a, errorIntervals: _ei, ...rest } = inter;
    publicInteraction = rest;
  } else if (inter.kind === 'coordinate-entry') {
    const { respostas: _r, erros: _er, ...rest } = inter;
    publicInteraction = rest;
  } else if (inter.kind === 'drag-points') {
    const { alvo, ...rest } = inter;
    publicInteraction = { ...rest, alvo: { describe: alvo.describe } };
  } else if (inter.kind === 'error-step') {
    const { passoErrado: _pe, erro: _e, explicacao: _x, ...rest } = inter;
    publicInteraction = rest;
  } else if (inter.kind === 'order') {
    const { ordem: _o, erros: _er, ...rest } = inter;
    publicInteraction = rest;
  } else if (inter.kind === 'expression-entry') {
    const { chave: _c, erros: _er, amostras: _am, ...rest } = inter;
    publicInteraction = rest;
  } else if (inter.kind === 'control-fit') {
    const { alvo, ...rest } = inter;
    publicInteraction = { ...rest, alvo: { describe: alvo.describe } };
  } else if (inter.kind === 'region-select') {
    const { chave: _c, erros: _er, diagnosticos: _d, analisar: _a, ...rest } = inter;
    publicInteraction = rest;
  } else if (inter.kind === 'balance-ops') {
    const { alvo, valor: _v, ...rest } = inter;
    publicInteraction = { ...rest, alvo: { describe: alvo.describe } };
  } else {
    const { target: _t, perturbable: _p, ...rest } = inter;
    publicInteraction = { ...rest, target: { describe: inter.target.describe } };
  }

  const { interaction: _i, rationale: _r, difficulty, ...rest } = i;
  return {
    ...rest,
    interaction: publicInteraction,
    difficulty: { provisionalB: difficulty.provisionalB, status: difficulty.status },
  };
}
